import React, { useState, useEffect, useMemo } from 'react';
import { Search, Eye, EyeOff } from 'lucide-react';
import { API_URL, adminFetch } from './shared';
import { StockSummaryCards, CostInputRows } from './stockTabParts';

const abbrevSize = (s) => s.replace(/^Adulto /, 'A-').replace(/^Niño /, 'N-');

// Numeric sizes (2-4, 6-8, 10-12…) sorted ascending first, rest keep appearance order
const leadNum = (s) => { const m = s.match(/\d+/); return m ? parseInt(m[0], 10) : null; };
const orderSizes = (arr) => {
  const nums = arr.filter(s => leadNum(s) !== null).sort((a, b) => leadNum(a) - leadNum(b));
  return [...nums, ...arr.filter(s => leadNum(s) === null)];
};
const shortName = (n) => (n || '').replace(/^Tzitzit /, '');

const isKidsSize = (s) => /\d/.test(s) || s.startsWith('Niño') || s.includes('Boys');
const unitPrice = (p, size) => {
  if (isKidsSize(size)) {
    return p.pricing_rules?.kids_price_mxn ?? p.kids_price_mxn
      ?? (p.kids_discount_mxn ? (p.price_mxn || 0) - p.kids_discount_mxn : (p.price_mxn || 0));
  }
  return p.price_mxn || 0;
};
const unitCost = (p, size) => (isKidsSize(size) ? (p.cost_kids_mxn || 0) : (p.cost_adult_mxn || 0));
const fmtMoney = (v) => `$${Math.round(v).toLocaleString('es-MX')}`;

const cellCls = (qty, th) => {
  if (qty <= 0) return 'bg-red-50 text-red-700 border-red-200';
  if (qty <= th) return 'bg-amber-50 text-amber-800 border-amber-200';
  return 'bg-emerald-50 text-emerald-800 border-emerald-200';
};

const StockCell = ({ product, size, onCommit, disabled }) => {
  const qty = parseInt((product.stock_qty || {})[size], 10) || 0;
  const th = product.low_stock_threshold ?? 3;
  return (
    <input
      key={`${product.product_id}-${size}-${qty}`}
      type="number" min="0" defaultValue={qty} disabled={disabled}
      onBlur={(e) => {
        const v = Math.max(0, parseInt(e.target.value, 10) || 0);
        if (v !== qty) onCommit(product, size, v);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      className={`w-12 text-center text-xs font-bold rounded border py-1 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50 ${cellCls(qty, th)} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
      data-testid={`stock-matrix-cell-${product.product_id}-${size}`}
    />
  );
};

export const StockTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [search, setSearch] = useState('');
  const [sizeFilter, setSizeFilter] = useState('all');
  const [showHidden, setShowHidden] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/products?_t=${Date.now()}`, { cache: 'no-store' });
      if (res.ok) setProducts(await res.json());
    } catch (e) { console.error('Stock fetch failed:', e); }
    setLoading(false);
  };
  useEffect(() => { fetchProducts(); }, []);

  const allTracked = useMemo(() => products.filter(p =>
    (p.sizes?.es || []).length > 0 && Object.keys(p.stock_qty || {}).some(k => !k.includes('|'))
  ), [products]);
  const hiddenCount = useMemo(() => allTracked.filter(p => p.stock_hidden).length, [allTracked]);
  const tracked = useMemo(() => showHidden ? allTracked : allTracked.filter(p => !p.stock_hidden), [allTracked, showHidden]);
  const untracked = useMemo(() => products.filter(p => !allTracked.includes(p)), [products, allTracked]);

  const toggleStockHidden = async (p) => {
    const next = !p.stock_hidden;
    setSaving(p.product_id);
    try {
      const res = await adminFetch(`/api/products/${p.product_id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stock_hidden: next }),
      });
      if (res.ok) setProducts(prev => prev.map(x => x.product_id === p.product_id ? { ...x, stock_hidden: next } : x));
      else alert('Error al guardar.');
    } catch (e) { alert('No se pudo conectar al servidor.'); }
    finally { setSaving(null); }
  };

  const visibleProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tracked;
    return tracked.filter(p => (p.name?.es || '').toLowerCase().includes(q));
  }, [tracked, search]);

  const allSizes = useMemo(() => {
    const seen = [];
    visibleProducts.forEach(p => (p.sizes?.es || []).forEach(s => { if (!seen.includes(s)) seen.push(s); }));
    return orderSizes(seen);
  }, [visibleProducts]);

  const visibleSizes = sizeFilter === 'all' ? allSizes : allSizes.filter(s => s === sizeFilter);

  // Global summary (all tracked products, unaffected by filters)
  const summary = useMemo(() => {
    let units = 0, low = 0, oos = 0, costValue = 0, retailValue = 0;
    tracked.forEach(p => {
      const th = p.low_stock_threshold ?? 3;
      (p.sizes?.es || []).forEach(size => {
        const n = parseInt((p.stock_qty || {})[size], 10) || 0;
        units += n;
        if (n <= 0) oos += 1;
        else if (n <= th) low += 1;
        costValue += n * unitCost(p, size);
        retailValue += n * unitPrice(p, size);
      });
    });
    return { units, low, oos, costValue, retailValue };
  }, [tracked]);

  const commitCell = async (product, size, value) => {
    const clean = Object.fromEntries(Object.entries(product.stock_qty || {}).filter(([k]) => !k.includes('|')));
    const newQty = { ...clean, [size]: value };
    setSaving(product.product_id);
    try {
      const res = await adminFetch(`/api/products/${product.product_id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stock_qty: newQty }),
      });
      if (res.ok) {
        setProducts(prev => prev.map(p => p.product_id === product.product_id ? { ...p, stock_qty: newQty } : p));
      } else {
        alert('Error al guardar. Intenta de nuevo.');
      }
    } catch (e) {
      alert('No se pudo conectar al servidor.');
    } finally { setSaving(null); }
  };

  const commitCost = async (product, field, value) => {
    setSaving(product.product_id);
    try {
      const res = await adminFetch(`/api/admin/products/${product.product_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) {
        setProducts(prev => prev.map(p => p.product_id === product.product_id ? { ...p, [field]: value } : p));
      } else {
        alert('Error al guardar el costo.');
      }
    } catch (e) {
      alert('No se pudo conectar al servidor.');
    } finally { setSaving(null); }
  };

  const exportCSV = () => {
    const header = ['Talla', ...tracked.map(p => `"${(p.name?.es || '').replace(/"/g, '""')}"`), 'Total talla', 'Costo', 'Precio'];
    const sizes = [];
    tracked.forEach(p => (p.sizes?.es || []).forEach(s => { if (!sizes.includes(s)) sizes.push(s); }));
    const ordered = orderSizes(sizes);
    const rows = ordered.map(size => {
      let total = 0, cost = 0, retail = 0;
      const cells = tracked.map(p => {
        if (!(p.sizes?.es || []).includes(size)) return '';
        const n = parseInt((p.stock_qty || {})[size], 10) || 0;
        total += n;
        cost += n * unitCost(p, size);
        retail += n * unitPrice(p, size);
        return n;
      });
      return [`"${size}"`, ...cells, total, Math.round(cost), Math.round(retail)].join(',');
    });
    const totals = ['"TOTAL"', ...tracked.map(p => (p.sizes?.es || []).reduce((s, sz) => s + (parseInt((p.stock_qty || {})[sz], 10) || 0), 0)), summary.units, Math.round(summary.costValue), Math.round(summary.retailValue)].join(',');
    const csv = '\uFEFF' + [header.join(','), ...rows, totals].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `stock_por_talla_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loading) return (
    <div className="text-center py-12 text-slate-500">
      <div className="inline-block w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mb-3"></div>
      <p>Cargando stock...</p>
    </div>
  );

  return (
    <div data-testid="stock-tab">
      <StockSummaryCards summary={summary} fmtMoney={fmtMoney} onExport={exportCSV} onRefresh={fetchProducts} />

      {/* Filters */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar modelo…"
            className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
            data-testid="stocktab-search" />
        </div>
        <select value={sizeFilter} onChange={(e) => setSizeFilter(e.target.value)}
          className="px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          data-testid="stocktab-size-filter">
          <option value="all">Todas las tallas</option>
          {allSizes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {hiddenCount > 0 && (
          <button onClick={() => setShowHidden(v => !v)}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${showHidden ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            data-testid="stocktab-show-hidden">
            {showHidden ? 'Ocultar descontinuados' : `Descontinuados (${hiddenCount})`}
          </button>
        )}
      </div>

      {/* Matrix: sizes x products */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mb-4">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="stocktab-matrix">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="sticky left-0 bg-slate-50 z-10 px-3 py-2 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider min-w-[90px]">Talla</th>
                {visibleProducts.map(p => (
                  <th key={p.product_id} className="px-2 py-2 text-center min-w-[72px]">
                    <div className="flex flex-col items-center gap-1 relative">
                      <button
                        onClick={() => toggleStockHidden(p)}
                        disabled={saving === p.product_id}
                        title={p.stock_hidden ? 'Restaurar a la tabla' : 'Quitar de la tabla (descontinuado)'}
                        className={`absolute -top-1 -right-1 p-0.5 rounded transition-colors ${p.stock_hidden ? 'text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50' : 'text-slate-300 hover:text-red-500 hover:bg-red-50'}`}
                        data-testid={`stocktab-hide-${p.product_id}`}
                      >
                        {p.stock_hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      </button>
                      <img src={p.image} alt="" className={`w-9 h-9 object-contain rounded bg-slate-100 ${p.stock_hidden ? 'opacity-40' : ''}`} />
                      <span className={`text-[9px] font-semibold leading-tight max-w-[72px] line-clamp-2 normal-case ${p.stock_hidden ? 'text-slate-400 line-through' : 'text-slate-600'}`}>{shortName(p.name?.es)}</span>
                    </div>
                  </th>
                ))}
                <th className="px-3 py-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider min-w-[60px]">Total</th>
                <th className="px-3 py-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider min-w-[70px]">Costo</th>
                <th className="px-3 py-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider min-w-[70px]">Precio</th>
              </tr>
            </thead>
            <tbody>
              <CostInputRows visibleProducts={visibleProducts} saving={saving} onCommitCost={commitCost} />
              {visibleSizes.map((size, i) => {
                let rowTotal = 0, rowCost = 0, rowPrice = 0;
                visibleProducts.forEach(p => {
                  if ((p.sizes?.es || []).includes(size)) {
                    const n = parseInt((p.stock_qty || {})[size], 10) || 0;
                    rowTotal += n;
                    rowCost += n * unitCost(p, size);
                    rowPrice += n * unitPrice(p, size);
                  }
                });
                return (
                  <tr key={size} className={`border-b border-slate-100 last:border-b-0 ${i % 2 ? 'bg-slate-50/40' : ''}`} data-testid={`stocktab-row-${size}`}>
                    <td className="sticky left-0 bg-inherit z-10 px-3 py-1.5 font-bold text-slate-700 whitespace-nowrap bg-white">{size}</td>
                    {visibleProducts.map(p => (
                      <td key={p.product_id} className="px-2 py-1.5 text-center">
                        {(p.sizes?.es || []).includes(size)
                          ? <StockCell product={p} size={size} onCommit={commitCell} disabled={saving === p.product_id} />
                          : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                    <td className={`px-3 py-1.5 text-center font-black ${rowTotal <= 0 ? 'text-red-600' : 'text-slate-700'}`}>{rowTotal}</td>
                    <td className="px-3 py-1.5 text-center font-semibold text-slate-500 whitespace-nowrap" data-testid={`row-cost-${size}`}>{fmtMoney(rowCost)}</td>
                    <td className="px-3 py-1.5 text-center font-bold text-emerald-700 whitespace-nowrap" data-testid={`row-price-${size}`}>{fmtMoney(rowPrice)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50">
                <td className="sticky left-0 bg-slate-50 z-10 px-3 py-2 text-[10px] font-bold text-slate-500 uppercase">Total</td>
                {visibleProducts.map(p => {
                  const t = (p.sizes?.es || []).reduce((s, sz) => s + (parseInt((p.stock_qty || {})[sz], 10) || 0), 0);
                  return <td key={p.product_id} className="px-2 py-2 text-center font-black text-slate-800">{t}</td>;
                })}
                <td className="px-3 py-2 text-center font-black text-cyan-700">
                  {visibleProducts.reduce((s, p) => s + (p.sizes?.es || []).reduce((x, sz) => x + (parseInt((p.stock_qty || {})[sz], 10) || 0), 0), 0)}
                </td>
                <td className="px-3 py-2 text-center font-black text-slate-600 whitespace-nowrap" data-testid="foot-cost">
                  {fmtMoney(visibleProducts.reduce((s, p) => s + (p.sizes?.es || []).reduce((x, sz) => x + (parseInt((p.stock_qty || {})[sz], 10) || 0) * unitCost(p, sz), 0), 0))}
                </td>
                <td className="px-3 py-2 text-center font-black text-emerald-700 whitespace-nowrap" data-testid="foot-price">
                  {fmtMoney(visibleProducts.reduce((s, p) => s + (p.sizes?.es || []).reduce((x, sz) => x + (parseInt((p.stock_qty || {})[sz], 10) || 0) * unitPrice(p, sz), 0), 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Per-size summary: which models exist in each size */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 mb-3" data-testid="stocktab-size-summary">
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Resumen por talla — modelos disponibles</p>
        <div className="space-y-1.5">
          {visibleSizes.map(size => {
            const models = visibleProducts
              .filter(p => (p.sizes?.es || []).includes(size))
              .map(p => ({ p, qty: parseInt((p.stock_qty || {})[size], 10) || 0 }));
            const total = models.reduce((s, m) => s + m.qty, 0);
            return (
              <div key={size} className="flex items-center gap-2 flex-wrap py-1 border-b border-slate-50 last:border-b-0" data-testid={`size-summary-${size}`}>
                <span className="text-xs font-black text-slate-700 min-w-[80px]">{abbrevSize(size)}</span>
                <span className={`inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded text-[10px] font-black ${total <= 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>{total}</span>
                <div className="flex items-center gap-1.5 flex-wrap flex-1">
                  {models.map(({ p, qty }) => (
                    <span key={p.product_id}
                      className={`inline-flex items-center gap-1 pl-0.5 pr-1.5 py-0.5 rounded-md border text-[10px] font-semibold ${cellCls(qty, p.low_stock_threshold ?? 3)}`}
                      title={`${p.name?.es} — ${qty} unidades`}>
                      <img src={p.image} alt="" className="w-5 h-5 object-contain rounded bg-white/70" />
                      {shortName(p.name?.es)} <strong>{qty}</strong>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {untracked.length > 0 && (
        <p className="text-[10px] text-slate-400 px-1">
          Sin control de stock ({untracked.length}): {untracked.map(p => p.name?.es).join(' · ')}
        </p>
      )}
      <p className="text-[10px] text-slate-400 px-1 mt-1">
        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" />saludable
        <span className="inline-block w-2 h-2 rounded-full bg-amber-500 ml-2 mr-1" />bajo (según alerta del producto)
        <span className="inline-block w-2 h-2 rounded-full bg-red-500 ml-2 mr-1" />agotado. Edita directo en la celda (Enter o clic fuera para guardar).
      </p>
    </div>
  );
};
